import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../shared/contexts/ThemeContext";
import { useToast } from "../../../shared/components/SharedUI";
import { SearchSelect } from "../../../shared/components/SearchSelect";
import AguaShell from "../components/AguaShell";
import { comprimirImagen, pesoLegible } from "../../../shared/utils/imagen";
import { crearLectura, getFilialesAgua, getLecturas } from "../services/aguaService";
import { AZUL, VERDE, AMBAR, aguaTheme, fechaCorta, hoyISO, m3 } from "../theme";

export default function AguaCaptura() {
  const { t }     = useTranslation();
  const { theme } = useTheme();
  const isLight   = theme === "light";
  const tema      = aguaTheme(isLight);
  const { showToast, toastEl } = useToast();

  const [filiales, setFiliales] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const [filialId, setFilialId] = useState("");
  const [fecha,    setFecha]    = useState(hoyISO());
  const [lectura,  setLectura]  = useState("");
  const [notas,    setNotas]    = useState("");
  const [foto,     setFoto]     = useState(null);
  const [fotoPrev, setFotoPrev] = useState(null);
  const [comprimiendo, setComprimiendo] = useState(false);
  const [error,    setError]    = useState("");
  // Cuando el backend rechaza por retroceso del medidor guardamos el mensaje
  // para ofrecer reenviar confirmando (medidor cambiado o que dio la vuelta).
  const [confirmar, setConfirmar] = useState("");

  const [lecturasDelDia, setLecturasDelDia] = useState([]);

  const camaraRef  = useRef(null);
  const galeriaRef = useRef(null);

  // ── Carga ────────────────────────────────────────────────────────────────
  function cargarFiliales() {
    return getFilialesAgua()
      .then(setFiliales)
      .catch(e => setError(e.message))
      .finally(() => setCargando(false));
  }

  function cargarDelDia(f) {
    getLecturas({ fecha: f })
      .then(d => setLecturasDelDia(d.results || d))
      .catch(() => setLecturasDelDia([]));
  }

  useEffect(() => { cargarFiliales(); }, []);
  useEffect(() => { cargarDelDia(fecha); }, [fecha]);

  // Liberar la URL del preview para no fugar memoria al cambiar de foto.
  useEffect(() => () => { if (fotoPrev) URL.revokeObjectURL(fotoPrev); }, [fotoPrev]);

  const seleccionada = filiales.find(f => String(f.id) === String(filialId));

  const opciones = useMemo(() => filiales.map(f => ({
    id: f.id,
    primary: f.idFilial,
    secondary: f.ultima_lectura != null
      ? `${t("agua.ultima")}: ${m3(f.ultima_lectura)} · ${fechaCorta(f.ultima_fecha)}`
      : t("agua.sinLecturasPrevias"),
  })), [filiales, t]);

  // Consumo estimado en vivo, para que el operador cache un dígito de más
  // antes de guardar en vez de descubrirlo en el reporte del mes siguiente.
  const consumoPreview = useMemo(() => {
    if (!seleccionada || seleccionada.ultima_lectura == null || lectura === "") return null;
    const n = parseFloat(String(lectura).replace(",", "."));
    if (Number.isNaN(n)) return null;
    return n - parseFloat(seleccionada.ultima_lectura);
  }, [seleccionada, lectura]);

  const idsConLectura = new Set(lecturasDelDia.map(l => String(l.filial)));
  const faltantes     = filiales.filter(f => !idsConLectura.has(String(f.id)));

  // ── Foto ──────────────────────────────────────────────────────────────────
  async function elegirFoto(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (fotoPrev) URL.revokeObjectURL(fotoPrev);
    // Se achica antes de guardarla en el estado: lo que se sube es la versión
    // liviana. Una foto de celular sale de 3 a 6 MB y así no pasa el servidor
    // web, además de tardar un mundo con datos móviles en plena ronda.
    setComprimiendo(true);
    try {
      const liviana = await comprimirImagen(f);
      setFoto(liviana);
      setFotoPrev(URL.createObjectURL(liviana));
    } finally {
      setComprimiendo(false);
    }
  }

  function quitarFoto() {
    if (fotoPrev) URL.revokeObjectURL(fotoPrev);
    setFoto(null);
    setFotoPrev(null);
    if (camaraRef.current)  camaraRef.current.value  = "";
    if (galeriaRef.current) galeriaRef.current.value = "";
  }

  // ── Guardar ───────────────────────────────────────────────────────────────
  async function guardar(e, permitirRetroceso = false) {
    if (e) e.preventDefault();
    setError(""); setConfirmar("");

    if (!filialId) { setError(t("agua.errFilial")); return; }
    if (lectura === "" || Number.isNaN(parseFloat(String(lectura).replace(",", ".")))) {
      setError(t("agua.errLectura")); return;
    }

    const fd = new FormData();
    fd.append("filial", filialId);
    fd.append("fecha", fecha);
    fd.append("lectura", String(lectura).replace(",", "."));
    if (notas) fd.append("notas", notas);
    if (foto)  fd.append("foto", foto);
    if (permitirRetroceso) fd.append("permitir_retroceso", "true");

    setGuardando(true);
    try {
      await crearLectura(fd);
      showToast(t("agua.guardada", { filial: seleccionada?.idFilial || "" }), true);
      // La filial queda lista: limpiamos la lectura y la foto pero dejamos la
      // fecha, porque el operador sigue con la siguiente casa de la misma ronda.
      setLectura(""); setNotas(""); quitarFoto(); setFilialId("");
      await cargarFiliales();
      cargarDelDia(fecha);
    } catch (err) {
      const d = err.data || {};
      const primero = v => (Array.isArray(v) ? v[0] : v);
      // El backend marca con codigo='retroceso' los casos que el operador puede
      // confirmar (medidor cambiado o que dio la vuelta) en vez de ser un error
      // definitivo. `codigo` es un campo de control: nunca se muestra como texto.
      if (primero(d.codigo) === "retroceso") {
        setConfirmar(primero(d.lectura) || err.message);
      } else {
        const detalle = Object.entries(d)
          .filter(([campo]) => campo !== "codigo")
          .map(([, v]) => primero(v))
          .filter(v => typeof v === "string")
          .join(" ");
        setError(detalle || err.message);
      }
    } finally {
      setGuardando(false);
    }
  }

  const labelStyle = {
    display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 0.7,
    textTransform: "uppercase", color: "var(--text-secondary)", marginBottom: 8,
  };

  return (
    <AguaShell
      titulo={t("agua.tituloCaptura")}
      descripcion={t("agua.descCaptura")}
      icono="fa-pen-to-square"
    >
      {toastEl}

      {/* ── Progreso de la ronda del día ── */}
      <div className="agua-card" style={{
        marginBottom: 14, boxShadow: tema.cardShadow,
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: tema.chipBg, border: `1px solid ${tema.chipBorder}`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <i className="fa-solid fa-list-check" style={{ color: AZUL, fontSize: 17 }} />
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
            {t("agua.progresoDia", {
              hechas: lecturasDelDia.length,
              total: filiales.length,
              fecha: fechaCorta(fecha),
            })}
          </div>
          <div style={{
            height: 7, borderRadius: 99, marginTop: 8, overflow: "hidden",
            background: isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.09)",
          }}>
            <div style={{
              height: "100%", borderRadius: 99,
              width: `${filiales.length ? (lecturasDelDia.length / filiales.length) * 100 : 0}%`,
              background: `linear-gradient(90deg,${AZUL},#38bdf8)`,
              transition: "width .3s",
            }} />
          </div>
        </div>
      </div>

      {/* ── Formulario ── */}
      <form onSubmit={guardar} className="agua-card" style={{ boxShadow: tema.cardShadow }}>

        <label style={labelStyle}>
          {t("agua.filial")} <span style={{ color: "#f87171" }}>*</span>
        </label>
        <div style={{ marginBottom: 18 }}>
          <SearchSelect
            options={opciones}
            value={filialId}
            onChange={id => setFilialId(id)}
            loading={cargando}
            placeholder={t("agua.buscarFilial")}
            icon="fa-house"
          />
        </div>

        {/* Referencia de la lectura anterior */}
        {seleccionada && (
          <div style={{
            marginBottom: 18, padding: "12px 14px", borderRadius: 11,
            background: tema.chipBg, border: `1px solid ${tema.chipBorder}`,
          }}>
            {seleccionada.ultima_lectura != null ? (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
                              textTransform: "uppercase", color: tema.chipText, marginBottom: 4 }}>
                  {t("agua.lecturaAnterior")}
                </div>
                <div style={{ fontSize: 19, fontWeight: 800, color: "var(--text-primary)" }}>
                  {m3(seleccionada.ultima_lectura)}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2 }}>
                  {fechaCorta(seleccionada.ultima_fecha)}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: tema.chipText }}>
                <i className="fa-solid fa-circle-info" style={{ marginRight: 7 }} />
                {t("agua.primeraLecturaAviso")}
              </div>
            )}
          </div>
        )}

        <div className="agua-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", marginBottom: 18 }}>
          <div>
            <label style={labelStyle}>{t("agua.fecha")} <span style={{ color: "#f87171" }}>*</span></label>
            <input type="date" className="form-control" required
                   value={fecha} max={hoyISO()}
                   onChange={e => setFecha(e.target.value)}
                   style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={labelStyle}>{t("agua.lectura")} <span style={{ color: "#f87171" }}>*</span></label>
            {/* inputMode decimal abre el teclado numérico en el celular */}
            <input type="text" inputMode="decimal" className="form-control" required
                   value={lectura} placeholder="0.00"
                   onChange={e => setLectura(e.target.value.replace(/[^\d.,]/g, ""))}
                   style={{ width: "100%", boxSizing: "border-box",
                            fontSize: 20, fontWeight: 800, letterSpacing: 0.5 }} />
          </div>
        </div>

        {/* Consumo calculado en vivo */}
        {consumoPreview != null && (
          <div style={{
            marginBottom: 18, padding: "12px 14px", borderRadius: 11,
            background: consumoPreview < 0 ? "rgba(239,68,68,0.09)" : "rgba(16,185,129,0.09)",
            border: `1px solid ${consumoPreview < 0 ? "rgba(239,68,68,0.3)" : "rgba(16,185,129,0.3)"}`,
            display: "flex", alignItems: "center", gap: 10,
          }}>
            <i className={`fa-solid ${consumoPreview < 0 ? "fa-triangle-exclamation" : "fa-droplet"}`}
               style={{ color: consumoPreview < 0 ? "#ef4444" : VERDE, fontSize: 16 }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
                            textTransform: "uppercase", color: "var(--text-secondary)" }}>
                {t("agua.consumoCalculado")}
              </div>
              <div style={{ fontSize: 17, fontWeight: 800,
                            color: consumoPreview < 0 ? "#ef4444" : VERDE }}>
                {m3(consumoPreview)}
              </div>
              {consumoPreview < 0 && (
                <div style={{ fontSize: 12, color: "#ef4444", marginTop: 2 }}>
                  {t("agua.consumoNegativo")}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Foto del medidor ── */}
        <label style={labelStyle}>{t("agua.foto")}</label>
        {fotoPrev ? (
          <div style={{ marginBottom: 18, position: "relative" }}>
            <img src={fotoPrev} alt={t("agua.foto")} style={{
              width: "100%", maxHeight: 280, objectFit: "cover",
              borderRadius: 12, border: `1px solid ${tema.chipBorder}`, display: "block",
            }} />
            <button type="button" onClick={quitarFoto} style={{
              position: "absolute", top: 10, right: 10, border: "none",
              borderRadius: 9, padding: "7px 12px", cursor: "pointer",
              background: "rgba(15,23,42,0.75)", color: "#fff",
              fontSize: 12, fontWeight: 700, backdropFilter: "blur(4px)",
            }}>
              <i className="fa-solid fa-xmark" style={{ marginRight: 6 }} />
              {t("agua.quitarFoto")}
            </button>
            {foto && (
              <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 6 }}>
                {pesoLegible(foto.size)} · lista para subir
              </div>
            )}
          </div>
        ) : comprimiendo ? (
          <div style={{
            marginBottom: 18, padding: "18px 14px", textAlign: "center",
            borderRadius: 12, border: `1px dashed ${tema.chipBorder}`,
            background: tema.chipBg, color: tema.chipText, fontWeight: 600, fontSize: 13,
          }}>
            <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8 }} />
            Preparando la foto…
          </div>
        ) : (
          <div className="agua-grid" style={{
            gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", marginBottom: 18,
          }}>
            {/* capture="environment" abre la cámara trasera directo en el celular */}
            <button type="button" onClick={() => camaraRef.current?.click()}
                    className="agua-btn" style={{
              background: tema.chipBg, color: tema.chipText,
              border: `1px dashed ${tema.chipBorder}`, padding: "18px 14px",
            }}>
              <i className="fa-solid fa-camera" /> {t("agua.tomarFoto")}
            </button>
            <button type="button" onClick={() => galeriaRef.current?.click()}
                    className="agua-btn" style={{
              background: "transparent", color: "var(--text-secondary)",
              border: "1px dashed var(--border-input)", padding: "18px 14px",
            }}>
              <i className="fa-solid fa-images" /> {t("agua.elegirGaleria")}
            </button>
          </div>
        )}
        <input ref={camaraRef}  type="file" accept="image/*" capture="environment"
               style={{ display: "none" }} onChange={elegirFoto} />
        <input ref={galeriaRef} type="file" accept="image/*"
               style={{ display: "none" }} onChange={elegirFoto} />

        <label style={labelStyle}>{t("agua.notas")}</label>
        <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
                  className="form-control" placeholder={t("agua.notasPlaceholder")}
                  style={{ width: "100%", resize: "vertical", boxSizing: "border-box", marginBottom: 18 }} />

        {error && (
          <div style={{
            marginBottom: 16, padding: "12px 14px", borderRadius: 11,
            background: "rgba(239,68,68,0.09)", border: "1px solid rgba(239,68,68,0.3)",
            color: "#ef4444", fontSize: 13, lineHeight: 1.5,
          }}>
            <i className="fa-solid fa-circle-exclamation" style={{ marginRight: 8 }} />
            {error}
          </div>
        )}

        {/* Confirmación de lectura menor a la anterior */}
        {confirmar && (
          <div style={{
            marginBottom: 16, padding: "14px", borderRadius: 11,
            background: "rgba(245,158,11,0.09)", border: "1px solid rgba(245,158,11,0.35)",
          }}>
            <div style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.5, marginBottom: 12 }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ color: AMBAR, marginRight: 8 }} />
              {confirmar}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" disabled={guardando}
                      onClick={() => guardar(null, true)}
                      className="agua-btn" style={{
                background: AMBAR, color: "#fff", padding: "10px 16px", fontSize: 13,
              }}>
                {t("agua.guardarIgual")}
              </button>
              <button type="button" onClick={() => setConfirmar("")}
                      className="agua-btn" style={{
                background: "transparent", color: "var(--text-secondary)",
                border: "1px solid var(--border-input)", padding: "10px 16px", fontSize: 13,
              }}>
                {t("agua.corregir")}
              </button>
            </div>
          </div>
        )}

        {/* Mientras se achica la foto no se puede guardar: si no, se manda la
            lectura sin ella y el operador tiene que volver a la casa. */}
        <button type="submit" disabled={guardando || comprimiendo} className="agua-btn" style={{
          width: "100%", background: AZUL, color: "#fff", padding: "15px",
          fontSize: 15, boxShadow: "0 6px 20px rgba(2,132,199,0.38)",
          opacity: (guardando || comprimiendo) ? 0.7 : 1,
        }}>
          <i className={`fa-solid ${(guardando || comprimiendo) ? "fa-spinner fa-spin" : "fa-floppy-disk"}`} />
          {comprimiendo ? "Preparando la foto…"
            : guardando ? t("common.cargando")
            : t("agua.guardarLectura")}
        </button>
      </form>

      {/* ── Filiales que faltan en la ronda del día ── */}
      {!cargando && faltantes.length > 0 && (
        <div className="agua-card" style={{ marginTop: 14, boxShadow: tema.cardShadow }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.7,
                        textTransform: "uppercase", color: "var(--text-secondary)", marginBottom: 12 }}>
            {t("agua.faltanHoy", { count: faltantes.length })}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {faltantes.map(f => (
              <button key={f.id} type="button" onClick={() => setFilialId(f.id)} style={{
                border: `1px solid ${String(filialId) === String(f.id) ? AZUL : "var(--border-input)"}`,
                background: String(filialId) === String(f.id) ? tema.chipBg : "transparent",
                color: String(filialId) === String(f.id) ? tema.chipText : "var(--text-secondary)",
                borderRadius: 9, padding: "7px 12px", fontSize: 13, fontWeight: 700,
                cursor: "pointer", transition: "all .15s",
              }}>
                {f.idFilial}
              </button>
            ))}
          </div>
        </div>
      )}
    </AguaShell>
  );
}
